import { useEffect, useState } from 'react';
import { SweepService } from '../service/sweepService';
import getStartAndEndOfDay, {
  downloadPdfFromBase64,
  errorToast,
  validateAndDecodeAuthToken,
} from '../utils/helper';
import { SweepSummaryParams } from '../pages/sweeps/sweeps.type';
import {
  EventType,
  OpeningHourSchedule,
  Player,
  RealtimeMessage,
  SweepEditHistory,
  SweepSignature,
  UpdateSweepResponse,
  UpdateUserResponse,
} from '@inplay/models';
import { useSweepSummary } from '../service/context/getSweepContext';
import { Sweep } from '../utils/helper.type';
import { AxiosResponse } from 'axios';
import { AuthTokenResponse } from '../service/service.type';
import { useWebSocket } from '../service/context/websocketContext';

export const useSweepsSummary = (
  params: SweepSummaryParams,
  realtimeEventsEnabled: boolean = true,
): {
  sweepsSummary: Sweep[];
  sweepsSummaryLoading: boolean;
  fetchSweepSummary: (
    localParams?: SweepSummaryParams,
    fromSocket?: boolean,
  ) => Promise<Sweep[] | undefined>;
  getVenueBranchOpeningHours: (
    venueBranchId: string,
  ) => Promise<OpeningHourSchedule | void>;
  updateSweepSignature: (
    sweepId: string,
    completedBy?: SweepSignature,
    reviewedBy?: SweepSignature,
    players?: Player[],
  ) => void;
} => {
  const [sweepSummaryLoading, setSweepSummaryLoading] =
    useState<boolean>(false);
  const { sweepSummary, setSweepSummary } = useSweepSummary();
  const websocket = useWebSocket();

  useEffect(() => {
    if (!params.venueBranchIds || !params.venueBranchIds.length) {
      console.log(
        '[useSweepsSummary] Skipping initialization – missing venueBranchIds',
      );
      return;
    }
    console.log('[useSweepsSummary] websocket useEffect ENTRY');
    websocket.service.onMessage((data: RealtimeMessage) => {
      console.log('[useSweepsSummary] ENTRY');
      switch (data.eventType) {
        // Sweep events
        case 'sweep':
          console.log(`[useSweepsSummary][websocket][sweep]`, data.eventType);
          fetchSweepSummary(undefined, true);
          break;
        // Add more cases as needed
        default:
          console.warn(
            `Unhandled WebSocket event: ${data.eventType}`,
            data.message,
          );
      }
    });
  }, [websocket, params.venueBranchIds?.length]);

  useEffect(() => {
    const decodedData: AuthTokenResponse | null = validateAndDecodeAuthToken();
    console.log('[useSweepSummary] venueBranchIds useEffect', {
      decodedData: !!decodedData,
      venueBranchIds: params.venueBranchIds,
      isConnected: websocket.isConnected,
    });

    if (
      decodedData &&
      params.venueBranchIds &&
      params.venueBranchIds.length > 0 &&
      realtimeEventsEnabled &&
      websocket.isConnected
    ) {
      console.log(
        '[useSweepSummary] Sending WebSocket message - connection ready',
        {
          decodedData,
          venueBranchIds: params.venueBranchIds,
        },
      );
      websocket.service.sendMessage({
        eventType: EventType.SWEEP,
        organizationUnitId: params.venueBranchIds[0],
        userId: decodedData.sub,
      });
    } else if (
      decodedData &&
      params.venueBranchIds &&
      params.venueBranchIds.length > 0 &&
      !websocket.isConnected
    ) {
      console.log('[useSweepSummary] Waiting for WebSocket connection...');
    }
  }, [params.venueBranchIds?.length, websocket.isConnected]);

  const fetchSweepSummary = async (
    localParams?: SweepSummaryParams,
    fromSocket?: boolean,
  ) => {
    const { startTime, endTime, venueBranchIds } = localParams ?? params;
    console.log(
      `[useSweepsSummary][fetchSweepSummary] Before calling fetchSweepSummary...`,
    );
    if (!fromSocket) setSweepSummaryLoading(true);

    try {
      const sweepService: SweepService = new SweepService();
      const sweepSummaryResponse: Sweep[] = await sweepService.getSweepSummary(
        startTime ?? getStartAndEndOfDay(new Date()).startTime ?? '',
        endTime ?? getStartAndEndOfDay(new Date()).endTime ?? '',
        venueBranchIds ?? [],
      );
      console.log(
        `[useUsersSummary][fetchSweepSummary] After calling fetchSweepSummary`,
        { sweepSummaryResponse },
      );

      setSweepSummary(sweepSummaryResponse);
      return sweepSummaryResponse;
    } catch (error: unknown) {
      errorToast(error);
      console.error(`[useUsersSummary][fetchSweepSummary] Error:`, error);
      setSweepSummary([]);
    } finally {
      console.log(`[useUsersSummary][fetchSweepSummary] finally block`);
      setSweepSummaryLoading(false);
    }
  };

  const getVenueBranchOpeningHours = async (venueBranchId: string) => {
    try {
      const sweepService: SweepService = new SweepService();
      return await sweepService.getVenueBranchOpeningHours(venueBranchId);
    } catch (error: unknown) {
      errorToast(error);
    }
  };

  const updateSweepSignature = (
    sweepId: string,
    completedBy?: SweepSignature,
    reviewedBy?: SweepSignature,
    players?: Player[],
  ) => {
    setSweepSummary((previousSweep) =>
      previousSweep.map((sweep: Sweep) => {
        if (sweep?._id === sweepId) {
          const updatedSweep = {
            ...sweep,
            ...(completedBy ? { completedBy } : {}),
            ...(reviewedBy ? { reviewedBy } : {}),
            ...(players ? { players } : {}),
            version: (sweep.version ?? 0) + 1,
          };
          return updatedSweep;
        }
        return sweep;
      }),
    );
  };

  return {
    sweepsSummary: sweepSummary ?? [],
    sweepsSummaryLoading: sweepSummaryLoading,
    fetchSweepSummary,
    getVenueBranchOpeningHours,
    updateSweepSignature,
  };
};

export const useSweepUpdate = (): {
  updateSweep: (
    sweepId: string,
    version: number,
    completedBy?: SweepSignature,
    reviewedBy?: SweepSignature,
    players?: Player[],
  ) => Promise<UpdateSweepResponse | null>;
  updateSweepLoading: boolean;
} => {
  const [updateSweepLoading, setUpdateSweepLoading] = useState<boolean>(false);
  const updateSweep = async (
    sweepId: string,
    version: number,
    completedBy?: SweepSignature,
    reviewedBy?: SweepSignature,
    players?: Player[],
  ): Promise<UpdateSweepResponse | null> => {
    console.log(`[useSweepUpdate] Before calling sweep update...`);
    setUpdateSweepLoading(true);
    try {
      const sweepService: SweepService = new SweepService();
      const sweepResponse: UpdateSweepResponse = await sweepService.updateSweep(
        sweepId,
        version,
        completedBy,
        reviewedBy,
        players,
      );
      console.log(`[useSweepUpdate] response...`, sweepResponse);
      return sweepResponse;
    } catch (error: unknown) {
      console.error(`[useSweepUpdate][ERROR]`, { error });
      errorToast(error);
      return null;
    } finally {
      setUpdateSweepLoading(false);
      console.log(`[useSweepUpdate] finally block`);
    }
  };

  return {
    updateSweep,
    updateSweepLoading,
  };
};

export const useApproveSummary = (): {
  approveSummary: (data: {
    sweepIds?: string[];
    venueBranchId: string;
    startTime?: number;
    endTime?: number;
    reviewedBy: {
      firstName: string;
      lastName: string;
      _id: string;
      notes: string;
      signature: string;
    };
  }) => Promise<UpdateUserResponse | undefined>;
  approveLoading: boolean;
} => {
  const [approveLoading, setApproveLoading] = useState<boolean>(false);

  const approveSummary = async (data: {
    sweepIds?: string[];
    venueBranchId: string;
    startTime?: number;
    endTime?: number;
    reviewedBy: {
      firstName: string;
      lastName: string;
      _id: string;
      notes: string;
      signature: string;
    };
  }): Promise<UpdateUserResponse | undefined> => {
    console.log(`[useApproveSummary] Before calling approveSweepReport...`);
    setApproveLoading(true);
    try {
      const sweepService: SweepService = new SweepService();
      const approveApiResponse: UpdateUserResponse =
        await sweepService.approveSweepReport(data);
      console.log(`[useApproveSummary] response`, approveApiResponse);
      return approveApiResponse;
    } catch (error: unknown) {
      console.error(`[useApproveSummary][ERROR]`, { error });
      errorToast(error);
    } finally {
      setApproveLoading(false);
      console.log(`[useApproveSummary] finally block`);
    }
  };

  return {
    approveSummary,
    approveLoading,
  };
};

export const useDeleteSweep = (): {
  deleteSweep: (sweepIds: string[]) => Promise<UpdateSweepResponse | undefined>;
  deleteSweepLoading: boolean;
} => {
  const [deleteSweepLoading, setDeleteSweepLoading] = useState<boolean>(false);

  const deleteSweep = async (sweepIds: string[]) => {
    if (!sweepIds) return;
    console.log(`[useDeleteSweep] Before calling...`);
    setDeleteSweepLoading(true);
    try {
      const sweepService: SweepService = new SweepService();

      const sweepDeleteResponse: UpdateSweepResponse =
        await sweepService.deleteSweep(sweepIds);
      console.log(`[useDeleteSweep] Response`, sweepDeleteResponse);
      return sweepDeleteResponse;
    } catch (error: unknown) {
      console.error(`[useDeleteSweep][ERROR]`, { error });
      throw error;
    } finally {
      console.log(`[useDeleteSweep] finally block`);
      setDeleteSweepLoading(false);
    }
  };

  return { deleteSweep, deleteSweepLoading };
};

export const useSweepHistory = (): {
  sweepHistory: SweepEditHistory;
  sweepHistoryLoading: boolean;
  fetchSweepHistory: (sweepId: string) => Promise<SweepEditHistory>;
} => {
  const [sweepHistory, setSweepHistory] = useState<SweepEditHistory>(
    {} as SweepEditHistory,
  );
  const [sweepHistoryLoading, setSweepHistoryLoading] =
    useState<boolean>(false);

  const fetchSweepHistory = async (
    sweepId: string,
  ): Promise<SweepEditHistory> => {
    console.log(
      '[useSweepHistory][fetchSweepHistory] Before calling fetchSweepHistory...',
      { sweepId },
    );
    setSweepHistoryLoading(true);

    try {
      const sweepService: SweepService = new SweepService();
      const sweepHistoryResponse: SweepEditHistory =
        await sweepService.getSweepHistory(sweepId);

      console.log(
        '[useSweepHistory][fetchSweepHistory] After calling fetchSweepHistory',
        { sweepHistoryResponse },
      );

      setSweepHistory(sweepHistoryResponse);
      return sweepHistoryResponse;
    } catch (error: unknown) {
      errorToast(error);
      console.error('[useSweepHistory][fetchSweepHistory] Error:', error);
      throw error;
    } finally {
      setSweepHistoryLoading(false);
      console.log('[useSweepHistory][fetchSweepHistory] finally block');
    }
  };

  return {
    sweepHistory,
    sweepHistoryLoading,
    fetchSweepHistory,
  };
};

export const useDownloadSweepReport = (): {
  downloadSweepReport: (sweepIds: string[]) => Promise<string[]>;
  downloadSweepReportLoading: boolean;
} => {
  const [downloadSweepReportLoading, setDownloadSweepReportLoading] =
    useState<boolean>(false);

  const downloadSweepReport = async (sweepIds: string[]): Promise<string[]> => {
    console.log(`[useDownloadSweepReport] Before calling...`, { sweepIds });
    setDownloadSweepReportLoading(true);

    try {
      const downloadSweepService: SweepService = new SweepService();

      const downloadSweepResponse: AxiosResponse<string[]> =
        await downloadSweepService.downloadSweepReport(sweepIds);

      console.log(`[useDownloadSweepReport] Response`, downloadSweepResponse);

      const base64Pdfs: string[] = downloadSweepResponse?.data ?? [];

      if (base64Pdfs.length === 0) {
        throw new Error('No PDF data returned from API');
      }

      base64Pdfs.forEach((pdfBase64: string, index: number) => {
        downloadPdfFromBase64(pdfBase64, `Sweep Report_${index + 1}`);
      });

      return base64Pdfs;
    } catch (error) {
      console.error(`[useDownloadSweepReport][ERROR]`, error);
      errorToast(error);
      throw error;
    } finally {
      setDownloadSweepReportLoading(false);
      console.log(`[useDownloadSweepReport] finally block`);
    }
  };

  return { downloadSweepReport, downloadSweepReportLoading };
};
